import { AxiosResponse } from 'axios';
import axiosInstance from '../../../axiosInstance';
import { setShowAlert } from '../../../state/utilts/utilsSlice';
import { TOAST_TYPE } from '../../../utils/constantTypes';

import type { AppDispatch } from '../../store';
import { GetGPSDataPayload, GPSDataFilters, SendGPSEmailPayloadType } from './types';
import { GPSDataResponse, GPSRangeDataResponse } from '../../gps/interfaces';

/** Maps Redux filters to GET /gps-data query params (single place for same-day → `date`). */
export const filtersToGpsQueryParams = (
  filters: GPSDataFilters | undefined,
): Record<string, string> => {
  if (!filters?.officeId) {
    return {};
  }

  const params: Record<string, string> = { officeId: filters.officeId };

  if (filters.date) {
    params.date = filters.date;
    return params;
  }

  const { startDate, endDate } = filters;
  if (startDate && endDate) {
    if (startDate === endDate) {
      params.date = startDate;
    } else {
      params.startDate = startDate;
      params.endDate = endDate;
    }
  }

  return params;
};

export type GetGPSDataRequestOptions = {
  dispatch: AppDispatch;
  signal?: AbortSignal;
};

type GetGPSDataPayloadWithRange = Omit<GetGPSDataPayload, 'filters'> & {
  filters?: GetGPSDataPayload['filters'] & {
    startDate?: string;
    endDate?: string;
  };
};

export const getGPSData = async (
  payload: GetGPSDataPayloadWithRange,
  { dispatch, signal }: GetGPSDataRequestOptions
): Promise<any> => {
  try {
    const response: AxiosResponse<
      GPSDataResponse | GPSRangeDataResponse
    > = await axiosInstance.get('/gps-data', {
      params: filtersToGpsQueryParams(payload.filters),
      headers: {
        Authorization: `Bearer ${payload.accessToken}`,
      },
      ...(signal ? { signal } : {}),
    });
    return response.data;
  } catch (err) {
    dispatch(
      setShowAlert({
        show: true,
        message: err?.response?.data?.msg,
        type: TOAST_TYPE.ERROR,
      }),
    );
    throw new Error(err?.response?.data?.msg);
  }
};

export const sendGPSEmail = async (
  payload: SendGPSEmailPayloadType,
  { dispatch }: { dispatch: AppDispatch },
): Promise<void> => {
  try {
    await axiosInstance.post(
      'send-gps-email',
      { ...payload.data },
      {
        headers: {
          Authorization: `Bearer ${payload.accessToken}`,
        },
      },
    );
    dispatch(
      setShowAlert({
        show: true,
        message: 'Email sent successfully.',
        type: TOAST_TYPE.SUCCESS,
      }),
    );
  } catch (err) {
    console.log('An error occured while sending GPS email.', err);
    throw new Error(err?.response?.data?.msg);
  }
};
